import React, { useEffect, useMemo, useState } from 'react';
import './Favorites.css';
import { useAuth } from '../../AuthContext';
import { apiUrl } from '../../api/client';
import SiteHeader from '../../shared/SiteHeader';
import type { ChatMessageDto } from '../../types/ChatMessageDto';
import Spinner from '../../shared/Spinner';
import { createApiClient } from '../../api/ApiClient';
import MessageFeedback from '../../shared/MessageFeedback';
import { ApiRoutes } from '../../types/ApiRoutes';
import { ChatMessageTypeIcon } from '../../utils/ChatMessageTypeIcon';

// Helper function to determine date group
const getDateGroup = (date: Date | string | null | undefined): string => {
  if (!date) {
    return 'Older';
  }

  const messageDate = new Date(date);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const monthAgo = new Date(today);
  monthAgo.setMonth(monthAgo.getMonth() - 1);

  const messageDateOnly = new Date(
    messageDate.getFullYear(),
    messageDate.getMonth(),
    messageDate.getDate()
  );

  if (messageDateOnly.getTime() === today.getTime()) {
    return 'Today';
  } else if (messageDateOnly.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  } else if (messageDate >= weekAgo) {
    return 'Previous Week';
  } else if (messageDate >= monthAgo) {
    return 'Previous Month';
  } else {
    return 'Older';
  }
};

const Favorites: React.FC = () => {
  const [favorites, setFavorites] = useState<ChatMessageDto[]>([]);
  const [responses, setResponses] = useState<Map<string, ChatMessageDto>>(new Map());
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [loadingResponses, setLoadingResponses] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');

  const { authorizedFetch } = useAuth();
  const api = useMemo(() => createApiClient(authorizedFetch), [authorizedFetch]);

  const groupedFavorites = useMemo(() => {
    const groups = new Map<string, ChatMessageDto[]>();
    const groupOrder = ['Today', 'Yesterday', 'Previous Week', 'Previous Month', 'Older'];

    favorites.forEach((message) => {
      const group = getDateGroup(message.timestamp);
      if (!groups.has(group)) {
        groups.set(group, []);
      }
      groups.get(group)?.push(message);
    });

    const orderedGroups = new Map<string, ChatMessageDto[]>();
    groupOrder.forEach((groupName) => {
      if (groups.has(groupName)) {
        const group = groups.get(groupName);
        if (group) {
          orderedGroups.set(groupName, group);
        }
      }
    });

    return orderedGroups;
  }, [favorites]);

  useEffect(() => {
    document.title = 'Walk It With Me - Favorites';

    void fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      setLoadingFavorites(true);
      setError('');
      const data = await api.get<ChatMessageDto[]>(apiUrl(ApiRoutes.Favorites), {
        errorMessage: 'Failed to fetch favorites',
      });
      setFavorites(data ?? []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoadingFavorites(false);
    }
  };

  const handleToggleMessage = async (message: ChatMessageDto) => {
    const messageId = message.messageId;
    const isExpanded = expandedMessages.has(messageId);

    if (isExpanded) {
      // Collapse
      setExpandedMessages((prev) => {
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
    } else {
      // Expand and load response if not already loaded
      setExpandedMessages((prev) => new Set(prev).add(messageId));

      if (!responses.has(messageId)) {
        setLoadingResponses((prev) => new Set(prev).add(messageId));
        try {
          const data = await api.get<ChatMessageDto>(
            apiUrl(`${ApiRoutes.Messages}/${messageId}/response`),
            { errorMessage: 'Failed to fetch response' }
          );
          setResponses((prev) => new Map(prev).set(messageId, data));
        } catch (err: unknown) {
          console.error('Failed to load response:', err);
        } finally {
          setLoadingResponses((prev) => {
            const next = new Set(prev);
            next.delete(messageId);
            return next;
          });
        }
      }
    }
  };

  const handleFavoriteRemoved = (messageId: string, isFavorite: boolean) => {
    if (!isFavorite) {
      setFavorites((prev) => prev.filter((m) => m.messageId !== messageId));
      setExpandedMessages((prev) => {
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
      setResponses((prev) => {
        const next = new Map(prev);
        next.delete(messageId);
        return next;
      });
    }
  };

  return (
    <>
      <SiteHeader />
      <div className="container py-5 favorites-page-v2">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <div className="favorites-header mb-4">
              <h2>
                <i className="bi bi-heart-fill text-danger me-2"></i>
                Favorite Messages
              </h2>
              <p className="text-muted">Click on any message to view its response.</p>
            </div>

            {loadingFavorites ? (
              <div className="text-center py-5">
                <Spinner visible={true} ariaLabel="Loading favorites..." />
              </div>
            ) : error ? (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            ) : favorites.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-heart fs-1 d-block mb-3"></i>
                <p>No favorite messages yet.</p>
                <small>Mark messages as favorites in your chats to see them here.</small>
              </div>
            ) : (
              <div className="favorites-accordion">
                {Array.from(groupedFavorites.entries()).map(([groupName, groupMessages]) => (
                  <div key={groupName} className="favorites-group mb-4">
                    <div className="favorites-group-header mb-3">
                      <h5>{groupName}</h5>
                    </div>
                    <div className="favorites-group-messages">
                      {groupMessages.map((message) => {
                        const isExpanded = expandedMessages.has(message.messageId);
                        const isLoadingResponse = loadingResponses.has(message.messageId);
                        const response = responses.get(message.messageId);

                        return (
                          <div key={message.messageId} className="favorite-message-card mb-3">
                            {/* User Message - Clickable */}
                            <div
                              className="chat-msg msg-user"
                              onClick={() => handleToggleMessage(message)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  handleToggleMessage(message);
                                }
                              }}
                              aria-expanded={isExpanded}
                              aria-controls={`response-${message.messageId}`}
                            >
                              <div className="bubble d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-start flex-grow-1">
                                  <i
                                    className={ChatMessageTypeIcon.getIconClass(
                                      message.messageType,
                                      'me-2 flex-shrink-0'
                                    )}
                                    aria-hidden="true"
                                  ></i>
                                  <span className="flex-grow-1">
                                    {message.content.replace(/<[^>]*>/g, '')}
                                  </span>
                                </div>
                                <i
                                  className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} ms-3 flex-shrink-0`}
                                  aria-hidden="true"
                                ></i>
                              </div>
                            </div>

                            {/* Assistant Response - Collapsible */}
                            <div
                              className={`collapse ${isExpanded ? 'show' : ''}`}
                              id={`response-${message.messageId}`}
                            >
                              <div className="chat-msg msg-assistant mt-2">
                                {isLoadingResponse ? (
                                  <div className="text-center py-3">
                                    <Spinner
                                      visible={true}
                                      ariaLabel="Loading response..."
                                      size="sm"
                                    />
                                  </div>
                                ) : response ? (
                                  <>
                                    <div
                                      className="bubble"
                                      dangerouslySetInnerHTML={{ __html: response.content }}
                                    />
                                    <MessageFeedback
                                      message={response}
                                      onFavoriteToggle={handleFavoriteRemoved}
                                    />
                                  </>
                                ) : (
                                  <div className="bubble text-muted">
                                    No response available for this message.
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Favorites;
